/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useMemo, useEffect } from "react";
import {
  HiOutlinePlus,
  HiTrash,
  HiPencilAlt,
  HiOutlineDeviceMobile,
} from "react-icons/hi";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useWorkspaceUsers } from "../../hooks";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";
import {
  Button,
  TableSkeleton,
  StandardTable,
  UserModal,
  DialogForm,
  PageLayout,
  SuccessPopup,
  HelpLabel,
  StoryblokHelpBlock,
} from "@components";
import { ITableRow, IWorkspaceAccessBase, Timestamp } from "../../types";
import { useTranslation } from "react-i18next";
import { createUser } from "../../api";
import { WorkspaceAccess } from "../../models";

export const WorkspaceUserPage: FC = function () {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isUpsertModalOpen, openUpsertModal] = useState(false);
  const [isDeleteModalOpen, openDeleteModal] = useState(false);
  const [userData, setUserData] = useState(WorkspaceAccess.default());
  const [isLoading, setLoading] = useState(true);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [isFromDrivers, setIsFromDrivers] = useState(false);
  const [showDriverSuccessPopup, setShowDriverSuccessPopup] = useState(false);
  const { activeWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const { userSession } = useUserSession();
  const query = useWorkspaceUsers();
  const { t } = useTranslation(["common", "user", "workspace"]);

  const isQueryLoading = useMemo(
    () => query.isLoading || query.isRefetching,
    [query],
  );

  const users = useMemo(() => query.data ?? [], [query]);

  const handleGoToAppStore = () => {
    setShowDriverSuccessPopup(false);
    window.open(
      "https://apps.apple.com/no/app/logibud/id6746366239?l=nb",
      "_blank",
    );
  };

  const handleGoToGooglePlay = () => {
    setShowDriverSuccessPopup(false);
    window.open(
      "https://play.google.com/store/apps/details?id=com.logibud.driverapp&hl=no",
      "_blank",
    );
  };

  // Check for 'new' query parameter to open user creation modal
  useEffect(() => {
    const newParam = searchParams.get("new");
    const fromDriversParam = searchParams.get("fromDrivers");
    if (newParam === "true" && !isQueryLoading) {
      setUserData(WorkspaceAccess.default());
      setIsFromDrivers(fromDriversParam === "true");
      openUpsertModal(true);
      // Remove query parameters from URL
      navigate(window.location.pathname, { replace: true });
    }
  }, [searchParams, isQueryLoading, navigate]);

  useEffect(() => {
    if (!isQueryLoading) {
      setLoading(false);
      setTableRows(
        users
          .filter((user) => !user.isApiKey)
          .map((user) => {
            return {
              id: user.email,
              cells: [
                {
                  id: "email",
                  children: user.email,
                },
                {
                  id: "role",
                  children: t(`workspace:roles.${user.role}`),
                },
                {
                  id: "isApiKey",
                  children: user.isApiKey ? t("common:yes") : t("common:no"),
                },
                {
                  id: "createdAt",
                  children: Timestamp.toDisplayDate(user.createdAt),
                },
              ],
              actions: [
                {
                  id: "edit",
                  icon: <HiPencilAlt />,
                  onActionClick: () => {
                    setUserData(user);
                    openUpsertModal(true);
                  },
                },
                {
                  id: "delete",
                  icon: <HiTrash />,
                  color: "failure",
                  onActionClick: () => {
                    setUserData(user);
                    openDeleteModal(true);
                  },
                },
              ],
            };
          }),
      );
    }
  }, [isQueryLoading, users]);

  return (
    <PageLayout>
      <div className="relative pb-20">
        <div className="flex flex-col sm:flex-row justify-between items-start p-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-text sm:text-2xl">
                {t("user:my_users")}
              </h1>
              <HelpLabel
                content={
                  <StoryblokHelpBlock
                    storySlug="logibud-tms-web/help-popups/workspace-users"
                    accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                  />
                }
                className="mt-1"
              />
            </div>
            <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
              {t("user:users_description")}
            </p>
          </div>
          <Button
            color="light"
            onClick={() => {
              setUserData(WorkspaceAccess.default());
              openUpsertModal(true);
            }}
            type="submit"
            className="mt-2 sm:mt-0 mb-4 sm:mb-0" // Adjusted margins
          >
            <HiOutlinePlus className="mr-2 h-5 w-5" />
            {t("user:new_user")}
          </Button>
        </div>

        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow text:white">
                {isLoading ? (
                  <TableSkeleton />
                ) : (
                  <StandardTable
                    header={[
                      {
                        id: "email",
                        children: t("user:users.email"),
                      },
                      {
                        id: "role",
                        children: t("user:role"),
                      },
                      {
                        id: "isApiKey",
                        children: t("user:users.is_api_key"),
                      },
                      {
                        id: "createdAt",
                        children: t("common:created_at"),
                      },
                    ]}
                    hasActions={true}
                    rows={tableRows}
                    shouldRenderRow={(row: ITableRow) => {
                      const user = users.find((u) => u.email === row.id);
                      return !user?.isApiKey;
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UserModal
        data={userData}
        isShowing={isUpsertModalOpen}
        onConfirm={async (formData: IWorkspaceAccessBase) => {
          if (WorkspaceAccess.isNew(userData)) {
            await createUser(
              userSession?.token.accessToken ?? "",
              activeWorkspace?.workspaceId ?? "",
              formData,
            );
            // Show driver success popup if this user was created from drivers page
            if (isFromDrivers) {
              setShowDriverSuccessPopup(true);
            }
          } else {
            await WorkspaceAccess.updateWorkspaceUser(userData, formData);
          }
          updateCacheKey();
          openUpsertModal(false);
          setLoading(true);
        }}
        onCancel={() => {
          openUpsertModal(false);
          setIsFromDrivers(false);
          // Remove query parameters when modal closes
          if (searchParams.get("new") === "true") {
            navigate(window.location.pathname, { replace: true });
          }
        }}
      />

      <DialogForm
        title={t("user:delete_user") + " " + userData.email + "?"}
        show={isDeleteModalOpen}
        toggleModal={() => openDeleteModal(false)}
        showHeader={false}
        buttonConfirmColor="failure"
        dismissible
        confirmButton={async () => {
          await WorkspaceAccess.deleteWorkspaceUser(userData);
          openDeleteModal(false);
          updateCacheKey();
          setLoading(true);
        }}
        buttonConfirmText={t("common:confirm")}
        buttonConfirmPrependIcon={<HiTrash className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:close")}
      />

      <SuccessPopup
        isOpen={showDriverSuccessPopup}
        title={t("driver:success.driver_ready")}
        message={t("driver:success.driver_app_message")}
        primaryAction={{
          text: t("driver:success.app_store"),
          onClick: handleGoToAppStore,
          variant: "success",
          icon: <HiOutlineDeviceMobile className="mr-2 h-5 w-5" />,
        }}
        secondaryAction={{
          text: t("driver:success.google_play"),
          onClick: handleGoToGooglePlay,
          variant: "secondary",
          icon: <HiOutlineDeviceMobile className="mr-2 h-5 w-5" />,
        }}
        onClose={() => setShowDriverSuccessPopup(false)}
      />
    </PageLayout>
  );
};
